import React, { useState } from 'react';
import { X } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const CreateBroadcastForm = ({ onClose, onSuccess }) => {
    const [name, setName] = useState('');
    const [messageText, setMessageText] = useState('');
    const [mediaPath, setMediaPath] = useState('');
    const [mediaFile, setMediaFile] = useState(null);
    const [ctaText, setCtaText] = useState('');
    const [ctaUrl, setCtaUrl] = useState('');
    const [scheduleMinutes, setScheduleMinutes] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleImageChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setMediaFile(f);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!messageText.trim()) {
            setError('Введите текст сообщения');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            let finalMediaPath = mediaPath;
            if (mediaFile) {
                const formData = new FormData();
                formData.append('file', mediaFile);
                const uploadRes = await fetch(`${API_BASE_URL}/api/broadcasts/upload-media`, {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                });
                if (!uploadRes.ok) {
                    const err = await uploadRes.json().catch(() => ({}));
                    throw new Error(err.detail || 'Ошибка загрузки изображения');
                }
                const up = await uploadRes.json();
                finalMediaPath = up.media_path;
            }
            const body = {
                name: name.trim() || null,
                message_text: messageText.trim(),
                media_path: finalMediaPath || null,
                cta_button_text: ctaText.trim() || null,
                cta_button_url: ctaUrl.trim() || null,
            };
            const createRes = await fetch(`${API_BASE_URL}/api/broadcasts/`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!createRes.ok) throw new Error('Ошибка создания рассылки');
            const campaign = await createRes.json();
            const minutes = parseInt(scheduleMinutes, 10);
            if (minutes > 0) {
                const scheduleRes = await fetch(
                    `${API_BASE_URL}/api/broadcasts/${campaign.id}/schedule-in?minutes=${minutes}`,
                    { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
                );
                if (!scheduleRes.ok) throw new Error('Ошибка планирования');
            }
            onSuccess?.();
        } catch (err) {
            setError(err.message || 'Ошибка');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-200 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-slate-800">Новая рассылка</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={24} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {error && (
                        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-600">{error}</div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Название (необязательно)</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            placeholder="Для себя в списке"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Текст сообщения *</label>
                        <textarea
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            rows={5}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            placeholder="Текст рассылки (поддерживается HTML)"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Картинка (необязательно)</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-50 file:text-indigo-700"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Текст кнопки (CTA)</label>
                            <input
                                type="text"
                                value={ctaText}
                                onChange={(e) => setCtaText(e.target.value)}
                                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                placeholder="Перейти"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Ссылка кнопки (CTA)</label>
                            <input
                                type="url"
                                value={ctaUrl}
                                onChange={(e) => setCtaUrl(e.target.value)}
                                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                placeholder="https://..."
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Запланировать через (минут)</label>
                        <input
                            type="number"
                            min="0"
                            value={scheduleMinutes}
                            onChange={(e) => setScheduleMinutes(e.target.value)}
                            className="w-32 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            placeholder="0 = не планировать"
                        />
                        <p className="text-xs text-slate-500 mt-1">0 — сохранить как черновик. 3–10 мин — отложенный старт с возможностью отменить.</p>
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {loading ? 'Создание...' : 'Создать рассылку'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateBroadcastForm;
